import { lazy } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { seoHeadFromPage } from "@/config/seoHead";
import { buildGridCompanyDescription } from "@/config/seoTitles";
import { scrapedPageQueryOptions } from "@/hooks/useScrapedPage";
import { supabase } from "@/integrations/supabase/client";
import { cleanArticleTitle } from "@/lib/articleContent";
import { lookupSlug, notFoundUnlessRedirected } from "@/lib/slugGuards";

const ElnatDetalj = lazy(() => import("@/pages/content/ElnatDetalj"));

/** Elområden (SE1–SE4) för ett elnätsbolag i grid_operators, matchat på namn. */
async function gridOperatorAreas(name: string): Promise<string[]> {
  const { data, error } = await supabase
    .from("grid_operators")
    .select("mba_areas")
    .eq("name", name)
    .eq("is_active", true)
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return (data?.mba_areas as string[] | null) ?? [];
}

export const Route = createFileRoute("/elnat/$slug")({
  // Ladda innehållet i SSR så sidan inte hoppar till efter hydrering.
  loader: async ({ params, context }) => {
    const path = `/elnat/${params.slug}`;
    const page = await lookupSlug(() =>
      context.queryClient.ensureQueryData(scrapedPageQueryOptions(path)),
    );
    // null = bolaget finns inte (404); undefined = uppslagningen misslyckades, rendera ändå.
    if (page === null) await notFoundUnlessRedirected(path);
    const name = cleanArticleTitle(page?.title) || "";
    // Elområdet ger beskrivningen något unikt utöver namnet; saknas det klarar den sig utan.
    const areas = name
      ? ((await lookupSlug(() => gridOperatorAreas(name))) ?? [])
      : [];
    return { page, name, areas };
  },
  head: ({ params, loaderData }) =>
    seoHeadFromPage(`/elnat/${params.slug}`, loaderData?.page, {
      ogType: "article",
      // Den importerade beskrivningen var formulärets samtyckestext på alla
      // elnätssidor – bygg en unik beskrivning av bolagsdatan i stället.
      ...(loaderData?.name && !loaderData.page?.seo_description
        ? {
            description: buildGridCompanyDescription(
              loaderData.name,
              loaderData.areas,
            ),
          }
        : {}),
    }),
  component: ElnatRoute,
});

function ElnatRoute() {
  const { page } = Route.useLoaderData();
  return <ElnatDetalj initialPage={page} />;
}
